
import React, { useEffect, useState } from 'react';
import { useAuth } from './AuthProvider';
import { AuthModal } from './AuthModal';
import { useI18n } from '../i18n/i18n';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

declare global {
  namespace JSX {
    interface IntrinsicElements {
      'stripe-pricing-table': {
        'pricing-table-id': string;
        'publishable-key': string;
        'customer-email'?: string;
      };
    }
  }
}

interface PricingSectionProps {
  showTitle?: boolean;
  className?: string;
}

export const PricingSection: React.FC<PricingSectionProps> = ({ 
  showTitle = true, 
  className = "" 
}) => {
  const { user } = useAuth();
  const { t } = useI18n();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authMode, setAuthMode] = useState<'login' | 'signup'>('signup');
  const [stripeLoaded, setStripeLoaded] = useState(false);

  // Load Stripe script
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://js.stripe.com/v3/pricing-table.js';
    script.async = true;
    script.onload = () => setStripeLoaded(true);
    document.head.appendChild(script);

    return () => {
      // Clean up script when component unmounts
      const existingScript = document.querySelector('script[src="https://js.stripe.com/v3/pricing-table.js"]');
      if (existingScript) {
        document.head.removeChild(existingScript);
      }
    };
  }, []);

  const handlePricingInteraction = () => {
    if (!user) {
      setShowAuthModal(true);
    }
    // Si el usuario está logueado, Stripe manejará automáticamente la suscripción
  };

  return (
    <div className={`py-16 bg-[#F9FAFB] ${className}`}>
      <style>{`
        /* Responsive styles for Stripe pricing table */
        stripe-pricing-table {
          width: 100% !important;
          max-width: 100% !important;
          background: transparent !important;
        }
        
        /* Remove any background overlays */
        stripe-pricing-table::part(container) {
          background: transparent !important;
        }
        
        stripe-pricing-table::part(pricing-table) {
          background: transparent !important;
        }
        
        /* Desktop and tablet horizontal layout */
        @media (min-width: 768px) {
          stripe-pricing-table::part(container) {
            display: flex !important;
            flex-direction: row !important;
            gap: 1rem !important;
            justify-content: center !important;
            align-items: stretch !important;
            background: transparent !important;
          }
          
          stripe-pricing-table::part(pricing-table) {
            display: flex !important;
            flex-direction: row !important;
            gap: 1rem !important;
            width: 100% !important;
            background: transparent !important;
          }
          
          stripe-pricing-table::part(pricing-card) {
            flex: 1 !important;
            min-width: 280px !important;
            max-width: 400px !important;
          }
        }
        
        /* Mobile vertical layout */
        @media (max-width: 767px) {
          stripe-pricing-table::part(container) {
            display: flex !important;
            flex-direction: column !important;
            gap: 1rem !important;
            align-items: center !important;
            background: transparent !important;
          }
          
          stripe-pricing-table::part(pricing-table) {
            display: flex !important;
            flex-direction: column !important;
            gap: 1rem !important;
            width: 100% !important;
            background: transparent !important;
          }
          
          stripe-pricing-table::part(pricing-card) {
            width: 100% !important;
            max-width: 400px !important;
            margin: 0 auto !important;
          }
        }
        
        /* Enhanced styling for all pricing cards */
        stripe-pricing-table::part(pricing-card) {
          border-radius: 12px !important;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1) !important;
          transition: all 0.3s ease-in-out !important;
          background: white !important;
        }
        
        /* Default styling for Free plan (first card) */
        stripe-pricing-table::part(pricing-card):nth-child(1) {
          border: 2px solid #e5e7eb !important;
        }
        
        /* Default styling for Plus plan (second card) */
        stripe-pricing-table::part(pricing-card):nth-child(2) {
          border: 2px solid #e5e7eb !important;
        }
        
        /* Default styling for Pro plan (third card) - distinctive blue border */
        stripe-pricing-table::part(pricing-card):nth-child(3) {
          border: 2px solid #3b82f6 !important;
          box-shadow: 0 2px 8px rgba(59, 130, 246, 0.2) !important;
        }
        
        /* Hover effects for Free plan */
        stripe-pricing-table::part(pricing-card):nth-child(1):hover {
          transform: translateY(-4px) !important;
          box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15) !important;
          border: 2px solid #9ca3af !important;
        }
        
        /* Hover effects for Plus plan */
        stripe-pricing-table::part(pricing-card):nth-child(2):hover {
          transform: translateY(-4px) !important;
          box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15) !important;
          border: 2px solid #9ca3af !important;
        }
        
        /* Enhanced hover effects for Pro plan */
        stripe-pricing-table::part(pricing-card):nth-child(3):hover {
          transform: translateY(-4px) !important;
          border: 2px solid #2563eb !important;
          box-shadow: 0 8px 25px rgba(59, 130, 246, 0.3) !important;
        }
        
        /* Remove any background colors or overlays that might interfere */
        stripe-pricing-table * {
          background-color: transparent !important;
        }
        
        /* Ensure card content backgrounds remain white */
        stripe-pricing-table::part(pricing-card) * {
          background-color: inherit !important;
        }
      `}</style>
      <div className="container mx-auto px-4 md:px-6">
        {showTitle && (
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-neutral-800 mb-4">
              {t('pricing.title')}
            </h2>
            <p className="text-lg text-neutral-600 max-w-2xl mx-auto">
              {t('pricing.subtitle')}
            </p>
          </div>
        )}

        {!user && (
          <div className="mb-8 text-center">
            <Card className="max-w-md mx-auto bg-blue-50 border-blue-200">
              <CardContent className="p-6">
                <h3 className="font-semibold text-lg mb-2 text-blue-800">
                  {t('pricing.loginRequired')}
                </h3>
                <p className="text-blue-600 mb-4 text-sm">
                  {t('pricing.loginMessage')}
                </p>
                <div className="flex gap-2 justify-center">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setAuthMode('login');
                      setShowAuthModal(true);
                    }}
                  >
                    {t('auth.login')}
                  </Button>
                  <Button
                    size="sm"
                    className="bg-blue-600 hover:bg-blue-700"
                    onClick={() => {
                      setAuthMode('signup');
                      setShowAuthModal(true);
                    }}
                  >
                    {t('auth.signup')}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        <div className="w-full max-w-7xl mx-auto">
          {stripeLoaded ? (
            <div 
              onClick={handlePricingInteraction}
              className="w-full overflow-x-auto"
            >
              <div className="min-w-full">
                <stripe-pricing-table
                  pricing-table-id="prctbl_1RmXDvP1BK1gITTKkYTwjE33"
                  publishable-key="pk_live_51RlwKzP1BK1gITTKnNIxTo38iNHrBPEv4qvMsefAqXwp9pI5b5XUiZb2m7UAs4ogqrC57LswOJkPxwUDsQnCkm4z00aCTmOI1l"
                  customer-email={user?.email || undefined}
                />
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto"></div>
              <p className="mt-4 text-neutral-600">{t('common.loading')}</p>
            </div>
          )}
        </div>

        <AuthModal
          isOpen={showAuthModal}
          onClose={() => setShowAuthModal(false)}
          mode={authMode}
          onModeChange={setAuthMode}
        />
      </div>
    </div>
  );
};
